import {
  Check,
  ChevronDown,
  ChevronUp,
  Dices,
  Layers,
  Trash2,
} from "lucide-react";
import {
  type ComponentProps,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { useStore } from "../../app/store.ts";
import type { AppState } from "../../app/storeTypes.ts";
import type {
  BevelLimitMethod,
  BlendArrayModifier,
  BlendBevelModifier,
  BlendBooleanModifier,
  BlendCurveModifier,
  BlendFollowObjectModifier,
  BlendInstanceModifier,
  BlendMirrorModifier,
  BlendModifier,
  BlendNoiseModifier,
  BlendShrinkwrapModifier,
  BlendSkinModifier,
  BlendSmoothModifier,
  BlendSolidifyModifier,
  BlendSubsurfModifier,
  BooleanOperation,
  CurveDeformAxis,
  NoiseLayer,
  NoiseType,
  SkinCapStyle,
  SkinStyle,
} from "../../blend-reader/types/BlendModifier.ts";
import { createDefaultNoiseLayer } from "../../blend-reader/types/BlendModifier.ts";
import { getBoneMap } from "../../mannequin/boneRegistry.ts";
import {
  ARRAY_MODIFIER_MAX_COUNT,
  clampArrayModifierCount,
} from "../../modeling/modifierLimits.ts";
import {
  FALLBACK_DIAGONAL,
  objectDiagonal,
} from "../../modeling/objectBounds.ts";
import { isCurveObject, isMannequinObject } from "../../modeling/types.ts";
import { toVertexGroupOptions } from "../../modeling/vertexGroupOptions.ts";
import { Button, IconButton } from "../Button.tsx";
import { useDeferredUndo } from "../hooks/useDeferredUndo.ts";
import { EyeClosedIcon, EyeOpenIcon } from "../Icons.tsx";
import { MenuItem } from "../menu/MenuItem.tsx";
import { Popover } from "../Popover.tsx";
import { beginSceneObjectNameEyedropper } from "../sceneObjectEyedropper.ts";
import {
  SliderRow as BaseSliderRow,
  CollapsiblePanel,
  CollapsibleSectionHeader,
  LabeledRow,
  ListControl,
  modKindColor,
  modKindLabel,
  SegmentedControl,
  SelectSearch,
  Toggle,
  ToggleChips,
} from "../shared.tsx";
import {
  COLOR_ACCENT,
  COLOR_BORDER,
  COLOR_BORDER_STRONG,
  COLOR_TEXT_DISABLED,
  COLOR_TEXT_FAINT,
  COLOR_TEXT_SECONDARY,
  COLOR_TEXT_SUBTLE,
  FONT_SIZE_SMALL,
  RADIUS_CONTROL,
} from "../tokens.ts";

// ---------------------------------------------------------------------------
// ModifiersPanel — top-level collapsible panel listing all modifiers
// ---------------------------------------------------------------------------

/** Stable empty set used as the fallback when no collapsed state exists for
 *  an object. Module-level so the selector returns the *same* reference on
 *  every call, preventing churn in zustand equality checks. */
const EMPTY_COLLAPSED: ReadonlySet<number> = new Set<number>();

const selectToggleModifierCollapsed = (s: AppState) =>
  s.toggleModifierCollapsed;

export function ModifiersPanel({
  objectName,
  baseModifiers,
  bare,
  isSceneObject,
}: {
  objectName: string;
  baseModifiers: BlendModifier[];
  bare?: boolean;
  /** When true, shows "Apply" controls (only for scene objects). */
  isSceneObject?: boolean;
}) {
  const modifierOverrides = useStore((s) => s.modifierOverrides);
  const setModifierEnabled = useStore((s) => s.setModifierEnabled);
  const setModifierProperty = useStore((s) => s.setModifierProperty);
  const addModifier = useStore((s) => s.addModifier);
  const removeModifier = useStore((s) => s.removeModifier);
  const moveModifier = useStore((s) => s.moveModifier);
  const applyModifier = useStore((s) => s.applyModifier);
  const pushUndo = useStore((s) => s.pushUndo);
  // Collapse state for modifier rows lives in the store (see `modifierUICollapsed`
  // in storeTypes.ts) so that re-mounting this panel — or viewing the same
  // object from a different surface — preserves what the user had folded away.
  const collapsed = useStore(
    (s) => s.modifierUICollapsed.get(objectName) ?? EMPTY_COLLAPSED,
  );
  const toggleCollapsed = useStore(selectToggleModifierCollapsed);
  const [addOpen, setAddOpen] = useState(false);
  const addBtnRef = useRef<HTMLButtonElement>(null);

  const objOverrides = modifierOverrides.get(objectName) ?? [];

  // Build merged modifier list (base + runtime)
  const maxLen = Math.max(baseModifiers.length, objOverrides.length);
  const modifiers: { mod: BlendModifier; idx: number; isUserAdded: boolean }[] =
    [];
  for (let i = 0; i < maxLen; i++) {
    const base = baseModifiers[i];
    const override = objOverrides[i];
    const mod = override?.runtimeModifier ?? base;
    if (!mod) continue;
    let merged = mod;
    if (override) {
      if (override.enabled !== undefined || override.props) {
        merged = { ...merged, ...(override.props ?? {}) } as typeof merged;
        if (override.enabled !== undefined)
          merged = { ...merged, enabled: override.enabled };
      }
    }
    modifiers.push({ mod: merged, idx: i, isUserAdded: !!override?.userAdded });
  }

  const modCount = modifiers.length;

  const content = (
    <>
      {modifiers.length === 0 && (
        <div
          style={{
            fontSize: FONT_SIZE_SMALL,
            color: COLOR_TEXT_FAINT,
            padding: "4px 0",
          }}
        >
          No modifiers
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {modifiers.map(({ mod, idx, isUserAdded }, displayIdx) => {
          const isEnabled = mod.enabled;
          const isCollapsed = collapsed.has(idx);
          const accent = modKindColor(mod.kind);
          const isFirst = displayIdx === 0;
          const isLast = displayIdx === modifiers.length - 1;
          // Some modifiers need to know whether an earlier enabled modifier
          // retopologizes away the polygon topology (currently: bevel).
          const precedingHasRetopo = modifiers
            .slice(0, displayIdx)
            .some(
              (m) =>
                m.mod.enabled &&
                (m.mod.kind === "subsurf" || m.mod.kind === "skin"),
            );

          return (
            <div
              key={`${mod.kind}_${idx}`}
              style={{
                border: `0.5px solid ${COLOR_BORDER}`,
                borderRadius: RADIUS_CONTROL,
                overflow: "hidden",
                opacity: isEnabled ? 1 : 0.45,
              }}
            >
              <CollapsibleSectionHeader
                title={mod.name || modKindLabel(mod.kind)}
                isCollapsed={isCollapsed}
                onToggle={() => toggleCollapsed(objectName, idx)}
                accentColor={accent}
                style={{ borderRadius: 0 }}
                actions={
                  <>
                    {/* Reorder controls */}
                    <span
                      onClick={() => {
                        if (!isFirst) {
                          pushUndo();
                          moveModifier(objectName, idx, -1);
                        }
                      }}
                      style={{
                        cursor: isFirst ? "default" : "pointer",
                        color: isFirst ? COLOR_TEXT_FAINT : COLOR_TEXT_SUBTLE,
                        opacity: isFirst ? 0.3 : 1,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <ChevronUp size={14} />
                    </span>
                    <span
                      onClick={() => {
                        if (!isLast) {
                          pushUndo();
                          moveModifier(objectName, idx, 1);
                        }
                      }}
                      style={{
                        cursor: isLast ? "default" : "pointer",
                        color: isLast ? COLOR_TEXT_FAINT : COLOR_TEXT_SUBTLE,
                        opacity: isLast ? 0.3 : 1,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <ChevronDown size={14} />
                    </span>

                    {/* Visibility toggle */}
                    <span
                      onClick={() => {
                        pushUndo();
                        setModifierEnabled(objectName, idx, !isEnabled);
                      }}
                      style={{
                        cursor: "pointer",
                        color: isEnabled ? COLOR_ACCENT : COLOR_TEXT_DISABLED,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {isEnabled ? <EyeOpenIcon /> : <EyeClosedIcon />}
                    </span>

                    {/* Apply (scene objects only) */}
                    {isSceneObject && (
                      <span
                        onClick={() => applyModifier(objectName, idx)}
                        title="Apply"
                        style={{
                          cursor: "pointer",
                          color: COLOR_TEXT_SUBTLE,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <Check size={12} />
                      </span>
                    )}

                    {/* Delete (user-added only) */}
                    {isUserAdded && (
                      <span
                        onClick={() => {
                          pushUndo();
                          removeModifier(objectName, idx);
                        }}
                        style={{
                          cursor: "pointer",
                          color: COLOR_TEXT_FAINT,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <Trash2 size={12} />
                      </span>
                    )}
                  </>
                }
              />

              {/* Settings (expanded by default) */}
              {!isCollapsed && (
                <ModifierSettings
                  objectName={objectName}
                  modifierIndex={idx}
                  mod={mod}
                  setProperty={setModifierProperty}
                  pushUndo={pushUndo}
                  precedingHasRetopo={precedingHasRetopo}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Add modifier */}
      <div style={{ marginTop: 6 }}>
        <Button
          ref={addBtnRef}
          onClick={() => setAddOpen(!addOpen)}
          variant="secondary"
          size="xs"
          fullWidth
          style={{
            border: `0.5px solid ${COLOR_BORDER_STRONG}`,
          }}
        >
          + Add Modifier
        </Button>
        <Popover
          anchorRef={addBtnRef}
          open={addOpen}
          onClose={() => setAddOpen(false)}
          style={{ borderRadius: RADIUS_CONTROL, minWidth: 160 }}
        >
          {(
            [
              "mirror",
              "subsurf",
              "solidify",
              "bevel",
              "array",
              "smooth",
              "skin",
              "instance",
              "shrinkwrap",
              "curve",
              "boolean",
              "noise",
              "followObject",
            ] as const
          ).map((kind) => (
            <MenuItem
              key={kind}
              icon={<span style={{ color: modKindColor(kind) }}>●</span>}
              label={modKindLabel(kind)}
              onClick={() => {
                pushUndo();
                addModifier(objectName, kind);
                setAddOpen(false);
              }}
            />
          ))}
        </Popover>
      </div>
    </>
  );

  if (bare) return content;

  return (
    <CollapsiblePanel
      id="modifiers"
      title="Modifiers"
      icon={<Layers size={11} />}
      summary={modCount > 0 ? `${modCount}` : undefined}
      defaultCollapsed={modCount === 0}
    >
      {content}
    </CollapsiblePanel>
  );
}

// ---------------------------------------------------------------------------
// Modifier settings — dispatches to per-kind settings components
// ---------------------------------------------------------------------------

/** Consistent label width for all modifier property rows. */
const MOD_LABEL_WIDTH = 65;

/**
 * World-space bounding-box diagonal of the owner mesh, used to scale
 * sliders whose ranges represent world-space distances (solidify
 * thickness, chain link length, boolean tolerance, etc.). A 5 cm trinket
 * gets a finely-grained slider; a 3 m couch gets a generous one.
 */
function useObjectDiagonal(objectName: string): number {
  const obj = useStore((s) => s.sceneObjects.get(objectName));
  return obj ? objectDiagonal(obj) : FALLBACK_DIAGONAL;
}

interface ModifierSliderContextValue {
  begin: () => void;
  end: () => void;
  runChange: (apply: () => void) => void;
}

const ModifierSliderContext = createContext<ModifierSliderContextValue | null>(
  null,
);

function SliderRow(props: ComponentProps<typeof BaseSliderRow>) {
  const slider = useContext(ModifierSliderContext);
  const handleChange = useCallback(
    (value: number) => {
      if (!slider) {
        props.onChange(value);
        return;
      }
      slider.runChange(() => props.onChange(value));
    },
    [props, slider],
  );

  return (
    <BaseSliderRow
      {...props}
      onChange={handleChange}
      onStart={slider?.begin}
      onEnd={slider?.end}
    />
  );
}

/** Shared props passed to every per-kind settings component. */
interface ModSetterProps {
  set: (prop: string, value: unknown) => void;
  setMany: (updates: Record<string, unknown>) => void;
}

function ModifierSettings({
  objectName,
  modifierIndex,
  mod,
  setProperty,
  pushUndo,
  precedingHasRetopo,
}: {
  objectName: string;
  modifierIndex: number;
  mod: BlendModifier;
  setProperty: (
    name: string,
    idx: number,
    prop: string,
    value: unknown,
  ) => void;
  pushUndo: () => void;
  precedingHasRetopo?: boolean;
}) {
  const liveUndo = useDeferredUndo();
  const liveChangeRef = useRef(false);

  const set = useCallback(
    (prop: string, value: unknown) => {
      if (!liveChangeRef.current) pushUndo();
      setProperty(objectName, modifierIndex, prop, value);
    },
    [modifierIndex, objectName, pushUndo, setProperty],
  );

  const setMany = useCallback(
    (updates: Record<string, unknown>) => {
      const entries = Object.entries(updates);
      if (entries.length === 0) return;
      if (!liveChangeRef.current) pushUndo();
      for (const [prop, value] of entries) {
        setProperty(objectName, modifierIndex, prop, value);
      }
    },
    [modifierIndex, objectName, pushUndo, setProperty],
  );

  const sliderContext = useMemo<ModifierSliderContextValue>(
    () => ({
      begin: liveUndo.pushOnce,
      end: liveUndo.reset,
      runChange: (apply) => {
        liveChangeRef.current = true;
        try {
          apply();
        } finally {
          liveChangeRef.current = false;
        }
      },
    }),
    [liveUndo],
  );

  const accent = modKindColor(mod.kind);

  return (
    <ModifierSliderContext.Provider value={sliderContext}>
      <div style={{ padding: "6px 10px 10px" }}>
        {mod.kind === "subsurf" && (
          <SubsurfSettings mod={mod} set={set} setMany={setMany} />
        )}
        {mod.kind === "mirror" && (
          <MirrorSettings mod={mod} set={set} setMany={setMany} />
        )}
        {mod.kind === "solidify" && (
          <SolidifySettings
            mod={mod}
            set={set}
            setMany={setMany}
            accent={accent}
            objectName={objectName}
          />
        )}
        {mod.kind === "array" && (
          <ArraySettings
            mod={mod}
            set={set}
            setMany={setMany}
            accent={accent}
            objectName={objectName}
            modifierIndex={modifierIndex}
          />
        )}
        {mod.kind === "skin" && (
          <SkinSettings
            mod={mod}
            set={set}
            setMany={setMany}
            accent={accent}
            objectName={objectName}
          />
        )}
        {mod.kind === "instance" && (
          <InstanceSettings
            mod={mod}
            set={set}
            setMany={setMany}
            objectName={objectName}
            accent={accent}
          />
        )}
        {mod.kind === "shrinkwrap" && (
          <ShrinkwrapSettings
            mod={mod}
            set={set}
            setMany={setMany}
            objectName={objectName}
            accent={accent}
          />
        )}
        {mod.kind === "curve" && (
          <CurveSettings
            mod={mod}
            set={set}
            setMany={setMany}
            objectName={objectName}
            accent={accent}
          />
        )}
        {mod.kind === "boolean" && (
          <BooleanSettings
            mod={mod}
            set={set}
            setMany={setMany}
            objectName={objectName}
            accent={accent}
          />
        )}
        {mod.kind === "noise" && (
          <NoiseSettings
            mod={mod}
            set={set}
            setMany={setMany}
            objectName={objectName}
            accent={accent}
          />
        )}
        {mod.kind === "followObject" && (
          <FollowObjectSettings
            mod={mod}
            set={set}
            setMany={setMany}
            objectName={objectName}
            accent={accent}
          />
        )}
        {mod.kind === "smooth" && (
          <SmoothSettings
            mod={mod}
            set={set}
            setMany={setMany}
            accent={accent}
            objectName={objectName}
          />
        )}
        {mod.kind === "bevel" && (
          <BevelSettings
            mod={mod}
            set={set}
            setMany={setMany}
            accent={accent}
            objectName={objectName}
            precedingHasRetopo={!!precedingHasRetopo}
          />
        )}
      </div>
    </ModifierSliderContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Subdivision Surface
// ---------------------------------------------------------------------------

const LEVEL_OPTIONS = [0, 1, 2, 3, 4].map((v) => ({
  label: String(v),
  value: v,
}));
const SUBDIV_TYPE_OPTIONS = [
  { label: "Smooth", value: 0 },
  { label: "Simple", value: 1 },
];

function SubsurfSettings({
  mod,
  set,
}: ModSetterProps & { mod: BlendSubsurfModifier }) {
  return (
    <>
      <LabeledRow label="Levels" labelWidth={MOD_LABEL_WIDTH}>
        <SegmentedControl
          options={LEVEL_OPTIONS}
          value={mod.levels}
          onChange={(v) => set("levels", v)}
        />
      </LabeledRow>
      <LabeledRow label="Type" labelWidth={MOD_LABEL_WIDTH}>
        <SegmentedControl
          options={SUBDIV_TYPE_OPTIONS}
          value={mod.subdivType}
          onChange={(v) => set("subdivType", v)}
        />
      </LabeledRow>
    </>
  );
}

// ---------------------------------------------------------------------------
// Mirror
// ---------------------------------------------------------------------------

function MirrorSettings({
  mod,
  set,
}: ModSetterProps & { mod: BlendMirrorModifier }) {
  return (
    <>
      <LabeledRow label="Axis" labelWidth={MOD_LABEL_WIDTH}>
        <ToggleChips
          items={[
            {
              label: "X",
              active: mod.axisX,
              onClick: () => set("axisX", !mod.axisX),
            },
            {
              label: "Y",
              active: mod.axisY,
              onClick: () => set("axisY", !mod.axisY),
            },
            {
              label: "Z",
              active: mod.axisZ,
              onClick: () => set("axisZ", !mod.axisZ),
            },
          ]}
        />
      </LabeledRow>
      <div style={{ marginTop: 6 }}>
        <Toggle
          label="Clipping"
          checked={mod.clipping}
          onChange={(v) => set("clipping", v)}
        />
      </div>
      <div style={{ marginTop: 6 }}>
        <Toggle
          label="Merge"
          checked={mod.merge}
          onChange={(v) => set("merge", v)}
        />
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Solidify
// ---------------------------------------------------------------------------

const DIRECTION_OPTIONS = [
  { label: "Outward", value: -1 },
  { label: "Center", value: 0 },
  { label: "Inward", value: 1 },
];

function SolidifySettings({
  mod,
  set,
  accent,
  objectName,
}: ModSetterProps & {
  mod: BlendSolidifyModifier;
  accent: string;
  objectName: string;
}) {
  // The vertex group belongs to the OWNER mesh — restricts which faces receive
  // the inner shell.  Hidden when the owner has no groups so the panel stays
  // clean for users who don't use vertex groups.
  const ownerVertexGroups = useStore(
    (s) => s.sceneObjects.get(objectName)?.vertexGroups,
  );
  const vertexGroupOptions = useMemo(
    () => toVertexGroupOptions(ownerVertexGroups),
    [ownerVertexGroups],
  );
  const diag = useObjectDiagonal(objectName);

  return (
    <>
      <SliderRow
        label="Thickness"
        value={mod.offset}
        min={0}
        max={0.25 * diag}
        step={0.001 * diag}
        displayValue={mod.offset.toFixed(3)}
        onChange={(v) => set("offset", v)}
        labelWidth={MOD_LABEL_WIDTH}
        accent={accent}
        dimChromeOnDrag
      />
      <LabeledRow label="Direction" labelWidth={MOD_LABEL_WIDTH}>
        <SegmentedControl
          options={DIRECTION_OPTIONS}
          value={mod.offsetDirection}
          onChange={(v) => set("offsetDirection", v)}
        />
      </LabeledRow>
      {vertexGroupOptions.length > 0 && (
        <SelectSearch
          label="Vertex Group"
          value={mod.vertexGroupId ?? null}
          options={vertexGroupOptions}
          onChange={(value) => set("vertexGroupId", value)}
          placeholder="All Faces"
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Smooth
// ---------------------------------------------------------------------------

function SmoothSettings({
  mod,
  set,
  accent,
  objectName,
}: ModSetterProps & {
  mod: BlendSmoothModifier;
  accent: string;
  objectName: string;
}) {
  const ownerVertexGroups = useStore(
    (s) => s.sceneObjects.get(objectName)?.vertexGroups,
  );
  const vertexGroupOptions = useMemo(
    () => toVertexGroupOptions(ownerVertexGroups),
    [ownerVertexGroups],
  );

  return (
    <>
      <SliderRow
        label="Strength"
        value={mod.strength}
        min={0}
        max={1}
        step={0.01}
        displayValue={mod.strength.toFixed(2)}
        onChange={(v) => set("strength", v)}
        labelWidth={MOD_LABEL_WIDTH}
        accent={accent}
        dimChromeOnDrag
      />
      <SliderRow
        label="Repeat"
        value={mod.iterations}
        min={1}
        max={10}
        step={1}
        displayValue={String(mod.iterations)}
        onChange={(v) => set("iterations", Math.round(v))}
        labelWidth={MOD_LABEL_WIDTH}
        accent={accent}
        dimChromeOnDrag
      />
      {vertexGroupOptions.length > 0 && (
        <SelectSearch
          label="Vertex Group"
          value={mod.vertexGroupId ?? null}
          options={vertexGroupOptions}
          onChange={(value) => set("vertexGroupId", value)}
          placeholder="All Vertices"
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Bevel
// ---------------------------------------------------------------------------

const BEVEL_LIMIT_OPTIONS: { label: string; value: BevelLimitMethod }[] = [
  { label: "None", value: "none" },
  { label: "Angle", value: "angle" },
  { label: "Group", value: "vertexGroup" },
];

function BevelSettings({
  mod,
  set,
  accent,
  objectName,
  precedingHasRetopo,
}: ModSetterProps & {
  mod: BlendBevelModifier;
  accent: string;
  objectName: string;
  precedingHasRetopo: boolean;
}) {
  const ownerVertexGroups = useStore(
    (s) => s.sceneObjects.get(objectName)?.vertexGroups,
  );
  const vertexGroupOptions = useMemo(
    () => toVertexGroupOptions(ownerVertexGroups),
    [ownerVertexGroups],
  );
  const diag = useObjectDiagonal(objectName);

  return (
    <>
      {precedingHasRetopo && (
        <div
          style={{
            margin: "0 0 8px",
            padding: "6px 8px",
            borderRadius: RADIUS_CONTROL,
            background: "rgba(220, 160, 60, 0.12)",
            border: "0.5px solid rgba(220, 160, 60, 0.4)",
            color: COLOR_TEXT_SECONDARY,
            fontSize: FONT_SIZE_SMALL,
            lineHeight: 1.35,
          }}
        >
          Preceding Subdivision or Skin removes polygon topology. Move Bevel
          above them to take effect.
        </div>
      )}
      <LabeledRow label="Limit" labelWidth={MOD_LABEL_WIDTH}>
        <SegmentedControl
          options={BEVEL_LIMIT_OPTIONS}
          value={mod.limitMethod}
          onChange={(v) => set("limitMethod", v)}
        />
      </LabeledRow>
      <SliderRow
        label="Width"
        value={mod.offset}
        min={0}
        max={0.25 * diag}
        step={0.001 * diag}
        displayValue={mod.offset.toFixed(3)}
        onChange={(v) => set("offset", v)}
        labelWidth={MOD_LABEL_WIDTH}
        accent={accent}
      />
      <SliderRow
        label="Segments"
        value={mod.segments}
        min={1}
        max={16}
        step={1}
        displayValue={String(mod.segments)}
        onChange={(v) => set("segments", Math.round(v))}
        labelWidth={MOD_LABEL_WIDTH}
        accent={accent}
      />
      <SliderRow
        label="Profile"
        value={mod.profile}
        min={0}
        max={1}
        step={0.01}
        displayValue={mod.profile.toFixed(2)}
        onChange={(v) => set("profile", v)}
        labelWidth={MOD_LABEL_WIDTH}
        accent={accent}
      />
      {mod.limitMethod === "angle" && (
        <SliderRow
          label="Angle"
          value={mod.angleLimit}
          min={0}
          max={180}
          step={1}
          displayValue={`${Math.round(mod.angleLimit)}°`}
          onChange={(v) => set("angleLimit", Math.round(v))}
          labelWidth={MOD_LABEL_WIDTH}
          accent={accent}
        />
      )}
      {mod.limitMethod === "vertexGroup" && vertexGroupOptions.length > 0 && (
        <SelectSearch
          label="Vertex Group"
          value={mod.vertexGroupId ?? null}
          options={vertexGroupOptions}
          onChange={(value) => set("vertexGroupId", value)}
          placeholder="Select group"
        />
      )}
      <div style={{ marginTop: 6 }}>
        <Toggle
          label="Clamp Overlap"
          checked={mod.clampOverlap}
          onChange={(v) => set("clampOverlap", v)}
        />
      </div>
      <div style={{ marginTop: 6 }}>
        <Toggle
          label="Mark Sharp"
          checked={mod.markSharp}
          onChange={(v) => set("markSharp", v)}
        />
      </div>
      <div style={{ marginTop: 6 }}>
        <Toggle
          label="Mark Seam"
          checked={mod.markSeam}
          onChange={(v) => set("markSeam", v)}
        />
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Skin
// ---------------------------------------------------------------------------

function SkinSettings({
  mod,
  set,
  setMany,
  accent,
  objectName,
}: ModSetterProps & {
  mod: BlendSkinModifier;
  accent: string;
  objectName: string;
}) {
  // The vertex group belongs to the OWNER mesh — restricts which wire edges
  // receive skin tubes (both endpoints must be in the group).  Hidden when
  // the owner has no groups so the panel stays clean for users who don't use
  // vertex groups.
  const ownerVertexGroups = useStore(
    (s) => s.sceneObjects.get(objectName)?.vertexGroups,
  );
  const vertexGroupOptions = useMemo(
    () => toVertexGroupOptions(ownerVertexGroups),
    [ownerVertexGroups],
  );
  const diag = useObjectDiagonal(objectName);

  const style: SkinStyle = mod.style ?? "smooth";

  return (
    <>
      <LabeledRow label="Style" labelWidth={MOD_LABEL_WIDTH}>
        <SegmentedControl<SkinStyle>
          options={[
            { label: "Smooth", value: "smooth" },
            { label: "Cable", value: "cable" },
            { label: "Bundle", value: "bundle" },
            { label: "Ribbed", value: "ribbed" },
            { label: "Chain", value: "chain" },
          ]}
          value={style}
          onChange={(v) => set("style", v)}
        />
      </LabeledRow>
      {/* Depth doubles as the wire radius for tube styles and is a no-op
          for chain (which uses absolute linkLength / wireThickness). Hide
          it in chain mode so users don't fiddle with a control that has
          no visible effect. */}
      {style !== "chain" && (
        <SliderRow
          label="Depth"
          value={mod.depth}
          min={0.0005 * diag}
          max={0.25 * diag}
          step={0.001 * diag}
          displayValue={mod.depth.toFixed(3)}
          onChange={(v) => set("depth", v)}
          labelWidth={MOD_LABEL_WIDTH}
          accent={accent}
          dimChromeOnDrag
        />
      )}
      <SliderRow
        label="Segments"
        value={mod.subdivisions}
        min={3}
        max={32}
        step={1}
        displayValue={String(mod.subdivisions)}
        parseValue={(s) => Math.max(3, Math.min(32, Math.round(parseFloat(s))))}
        onChange={(v) =>
          set("subdivisions", Math.max(3, Math.min(32, Math.round(v))))
        }
        labelWidth={MOD_LABEL_WIDTH}
        accent={accent}
        dimChromeOnDrag
      />
      {style === "cable" && (
        <>
          <SliderRow
            label="Strands"
            value={mod.strandCount ?? 3}
            min={2}
            max={24}
            step={1}
            displayValue={String(mod.strandCount ?? 3)}
            parseValue={(s) =>
              Math.max(2, Math.min(24, Math.round(parseFloat(s))))
            }
            onChange={(v) =>
              set("strandCount", Math.max(2, Math.min(24, Math.round(v))))
            }
            labelWidth={MOD_LABEL_WIDTH}
            accent={accent}
            dimChromeOnDrag
          />
          <SliderRow
            label="Twist"
            value={mod.twistRate ?? 3}
            min={0}
            max={20}
            step={0.1}
            displayValue={(mod.twistRate ?? 3).toFixed(1)}
            onChange={(v) => set("twistRate", v)}
            labelWidth={MOD_LABEL_WIDTH}
            accent={accent}
            dimChromeOnDrag
          />
        </>
      )}
      {style === "bundle" && (
        <>
          <SliderRow
            label="Wires"
            value={mod.strandCount ?? 3}
            min={2}
            max={24}
            step={1}
            displayValue={String(mod.strandCount ?? 3)}
            parseValue={(s) =>
              Math.max(2, Math.min(24, Math.round(parseFloat(s))))
            }
            onChange={(v) =>
              set("strandCount", Math.max(2, Math.min(24, Math.round(v))))
            }
            labelWidth={MOD_LABEL_WIDTH}
            accent={accent}
            dimChromeOnDrag
          />
          <SliderRow
            label="Diameter Jitter"
            value={mod.bundleJitter ?? 0.3}
            min={0}
            max={1}
            step={0.01}
            displayValue={(mod.bundleJitter ?? 0.3).toFixed(2)}
            onChange={(v) => set("bundleJitter", v)}
            labelWidth={MOD_LABEL_WIDTH}
            accent={accent}
            dimChromeOnDrag
          />
        </>
      )}
      {style === "ribbed" && (
        <>
          <SliderRow
            label="Frequency"
            value={mod.ribFrequency ?? 8}
            min={1}
            max={64}
            step={0.5}
            displayValue={(mod.ribFrequency ?? 8).toFixed(1)}
            onChange={(v) => set("ribFrequency", v)}
            labelWidth={MOD_LABEL_WIDTH}
            accent={accent}
            dimChromeOnDrag
          />
          <SliderRow
            label="Rib Depth"
            value={mod.ribDepth ?? 0.5}
            min={0}
            max={1}
            step={0.01}
            displayValue={(mod.ribDepth ?? 0.5).toFixed(2)}
            onChange={(v) => set("ribDepth", v)}
            labelWidth={MOD_LABEL_WIDTH}
            accent={accent}
            dimChromeOnDrag
          />
        </>
      )}
      {style === "chain" && (
        <>
          {/* Ranges chosen to span real physical chain dimensions: 2 mm
              jewelry links at the bottom, ~30 cm decorative props at the
              top. 1 m+ link lengths and 10 cm wire diameters are not
              representative of any actual chain. */}
          <SliderRow
            label="Link Length"
            value={mod.linkLength ?? 0.04}
            min={0.002}
            max={0.3}
            step={0.001}
            displayValue={(mod.linkLength ?? 0.04).toFixed(3)}
            onChange={(v) => set("linkLength", v)}
            labelWidth={MOD_LABEL_WIDTH}
            accent={accent}
            dimChromeOnDrag
          />
          <SliderRow
            label="Aspect"
            value={mod.linkAspect ?? 0.6}
            min={0.3}
            max={1}
            step={0.01}
            displayValue={(mod.linkAspect ?? 0.6).toFixed(2)}
            onChange={(v) => set("linkAspect", v)}
            labelWidth={MOD_LABEL_WIDTH}
            accent={accent}
            dimChromeOnDrag
          />
          <SliderRow
            label="Wire"
            value={mod.wireThickness ?? 0.005}
            min={0.0002}
            max={0.03}
            step={0.0001}
            displayValue={(mod.wireThickness ?? 0.005).toFixed(4)}
            onChange={(v) => set("wireThickness", v)}
            labelWidth={MOD_LABEL_WIDTH}
            accent={accent}
            dimChromeOnDrag
          />
          <SliderRow
            label="Overlap"
            value={mod.linkOverlap ?? 0.4}
            min={0}
            max={0.6}
            step={0.01}
            displayValue={(mod.linkOverlap ?? 0.4).toFixed(2)}
            onChange={(v) => set("linkOverlap", v)}
            labelWidth={MOD_LABEL_WIDTH}
            accent={accent}
            dimChromeOnDrag
          />
          <SliderRow
            label="Twist"
            value={mod.twistPerLink ?? 90}
            min={0}
            max={180}
            step={1}
            displayValue={`${Math.round(mod.twistPerLink ?? 90)}°`}
            parseValue={(s) =>
              Math.max(0, Math.min(180, Math.round(parseFloat(s))))
            }
            onChange={(v) =>
              set("twistPerLink", Math.max(0, Math.min(180, Math.round(v))))
            }
            labelWidth={MOD_LABEL_WIDTH}
            accent={accent}
            dimChromeOnDrag
          />
          <SliderRow
            label="Link Segs"
            value={mod.linkSegments ?? 24}
            min={8}
            max={48}
            step={1}
            displayValue={String(mod.linkSegments ?? 24)}
            parseValue={(s) =>
              Math.max(8, Math.min(48, Math.round(parseFloat(s))))
            }
            onChange={(v) =>
              set("linkSegments", Math.max(8, Math.min(48, Math.round(v))))
            }
            labelWidth={MOD_LABEL_WIDTH}
            accent={accent}
            dimChromeOnDrag
          />
        </>
      )}
      {/* End caps and Preserve Volume are tube-style concepts that don't
          apply to chain links — each link is a closed mesh with no open
          end to cap, and the volume-preservation hack only matters for
          continuous tubes that pinch at sharp bends. */}
      {style !== "chain" && (
        <>
          <LabeledRow label="End Caps" labelWidth={MOD_LABEL_WIDTH}>
            <SegmentedControl<SkinCapStyle>
              options={[
                { label: "None", value: "none" },
                { label: "Flat", value: "flat" },
                { label: "Ferrule", value: "ferrule" },
              ]}
              value={mod.capStyle ?? (mod.fillCaps ? "flat" : "none")}
              onChange={(v) => setMany({ capStyle: v, fillCaps: v !== "none" })}
            />
          </LabeledRow>
          <div style={{ marginTop: 6 }}>
            <Toggle
              label="Preserve Volume"
              checked={mod.preserveVolume}
              onChange={(v) => set("preserveVolume", v)}
            />
          </div>
        </>
      )}
      {vertexGroupOptions.length > 0 && (
        <SelectSearch
          label="Vertex Group"
          value={mod.vertexGroupId ?? null}
          options={vertexGroupOptions}
          onChange={(value) => set("vertexGroupId", value)}
          placeholder="All Vertices"
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Array
// ---------------------------------------------------------------------------

function ArraySettings({
  mod,
  set,
  accent,
  objectName,
  modifierIndex,
}: ModSetterProps & {
  mod: BlendArrayModifier;
  accent: string;
  objectName: string;
  modifierIndex: number;
}) {
  const sceneObjects = useStore((s) => s.sceneObjects);
  const setArrayOffsetObject = useStore((s) => s.setArrayOffsetObject);
  const pushUndo = useStore((s) => s.pushUndo);

  // Build options from all scene objects (they can be Empties or anything)
  const offsetOptions = useMemo(
    () =>
      Array.from(sceneObjects.values())
        .filter((o) => o.name !== objectName)
        .map((o) => ({ value: o.name, label: o.name })),
    [sceneObjects, objectName],
  );
  const diag = useObjectDiagonal(objectName);

  return (
    <>
      <SliderRow
        label="Count"
        value={mod.count}
        min={1}
        max={ARRAY_MODIFIER_MAX_COUNT}
        step={1}
        displayValue={String(mod.count)}
        parseValue={(s) => clampArrayModifierCount(parseFloat(s))}
        onChange={(v) => set("count", clampArrayModifierCount(v))}
        labelWidth={MOD_LABEL_WIDTH}
        accent={accent}
        dimChromeOnDrag
      />
      <div style={{ marginTop: 6 }}>
        <Toggle
          label="Relative Offset"
          checked={mod.useRelativeOffset}
          onChange={(v) => set("useRelativeOffset", v)}
        />
      </div>
      {mod.useRelativeOffset &&
        (["X", "Y", "Z"] as const).map((axis, i) => (
          <SliderRow
            key={axis}
            label={axis}
            value={mod.relativeOffset[i]}
            min={-4}
            max={4}
            step={0.01}
            displayValue={mod.relativeOffset[i].toFixed(2)}
            onChange={(v) => {
              const next: [number, number, number] = [...mod.relativeOffset];
              next[i] = v;
              set("relativeOffset", next);
            }}
            labelWidth={MOD_LABEL_WIDTH}
            accent={accent}
            dimChromeOnDrag
          />
        ))}
      <div style={{ marginTop: 6 }}>
        <Toggle
          label="Constant Offset"
          checked={mod.useConstantOffset}
          onChange={(v) => set("useConstantOffset", v)}
        />
      </div>
      {mod.useConstantOffset &&
        (["X", "Y", "Z"] as const).map((axis, i) => (
          <SliderRow
            key={axis}
            label={axis}
            value={mod.constantOffset[i]}
            min={-2 * diag}
            max={2 * diag}
            step={0.002 * diag}
            displayValue={mod.constantOffset[i].toFixed(2)}
            onChange={(v) => {
              const next: [number, number, number] = [...mod.constantOffset];
              next[i] = v;
              set("constantOffset", next);
            }}
            labelWidth={MOD_LABEL_WIDTH}
            accent={accent}
            dimChromeOnDrag
          />
        ))}
      <div style={{ marginTop: 6 }}>
        <SelectSearch
          label="Object"
          value={mod.offsetObjectName ?? null}
          options={offsetOptions}
          onChange={(v) => {
            pushUndo();
            setArrayOffsetObject(objectName, modifierIndex, v);
          }}
          placeholder="None"
          eyedropperPick={(onPick, onCancel) => {
            beginSceneObjectNameEyedropper(
              onPick,
              onCancel,
              (obj) => obj.name !== objectName,
            );
          }}
        />
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Instance
// ---------------------------------------------------------------------------

const PLACEMENT_OPTIONS = [
  { label: "Origin", value: "origin" as const },
  { label: "Vertices", value: "vertices" as const },
  { label: "Edges", value: "edges" as const },
  { label: "Faces", value: "faces" as const },
];

const ORIENTATION_OPTIONS = [
  { label: "Local", value: "local" as const },
  { label: "Normal", value: "normal" as const },
  { label: "Global", value: "global" as const },
];

const INSTANCE_LIMIT_OPTIONS = [
  { label: "64", value: 64 as const },
  { label: "128", value: 128 as const },
  { label: "256", value: 256 as const },
  { label: "512", value: 512 as const },
];

function InstanceSettings({
  mod,
  set,
  objectName,
  accent,
}: ModSetterProps & {
  mod: BlendInstanceModifier;
  objectName: string;
  accent: string;
}) {
  const sceneObjects = useStore((s) => s.sceneObjects);
  const sourceOptions = useMemo(
    () =>
      Array.from(sceneObjects.values())
        .filter((obj) => obj.name !== objectName && obj.vertices.length > 0)
        .map((obj) => ({ value: obj.name, label: obj.name })),
    [sceneObjects, objectName],
  );

  // The vertex group belongs to the OWNER mesh — the modifier restricts
  // placement to group members on *this* object, not on the source being
  // instanced. Hidden entirely when the owner has no groups so the panel
  // stays identical for users who don't use vertex groups.
  const ownerVertexGroups = useStore(
    (s) => s.sceneObjects.get(objectName)?.vertexGroups,
  );
  const vertexGroupOptions = useMemo(
    () => toVertexGroupOptions(ownerVertexGroups),
    [ownerVertexGroups],
  );

  const placementMode = mod.placementMode ?? "origin";
  const scatterEligible =
    placementMode === "edges" || placementMode === "faces";
  const scatterMode = (mod.scatterMode ?? false) && scatterEligible;

  return (
    <>
      <SelectSearch
        label="Object"
        value={mod.sourceObjectName ?? null}
        options={sourceOptions}
        onChange={(value) => set("sourceObjectName", value)}
        placeholder="None"
        eyedropperPick={(onPick, onCancel) => {
          beginSceneObjectNameEyedropper(
            onPick,
            onCancel,
            (obj) => obj.name !== objectName && obj.vertices.length > 0,
          );
        }}
      />
      {vertexGroupOptions.length > 0 && placementMode !== "origin" && (
        <SelectSearch
          label="Vertex Group"
          value={mod.vertexGroupId ?? null}
          options={vertexGroupOptions}
          onChange={(value) => set("vertexGroupId", value)}
          placeholder="All Vertices"
        />
      )}
      <LabeledRow label="Place" labelWidth={MOD_LABEL_WIDTH}>
        <SegmentedControl
          options={PLACEMENT_OPTIONS}
          value={placementMode}
          onChange={(v) => set("placementMode", v)}
        />
      </LabeledRow>
      <LabeledRow label="Orient" labelWidth={MOD_LABEL_WIDTH}>
        <SegmentedControl
          options={ORIENTATION_OPTIONS}
          value={mod.orientation ?? "local"}
          onChange={(v) => set("orientation", v)}
        />
      </LabeledRow>
      {placementMode !== "origin" && (
        <LabeledRow label="Limit" labelWidth={MOD_LABEL_WIDTH}>
          <SegmentedControl
            options={INSTANCE_LIMIT_OPTIONS}
            value={mod.instanceLimit ?? 64}
            onChange={(v) => set("instanceLimit", v)}
          />
        </LabeledRow>
      )}
      {scatterEligible && (
        <div style={{ marginTop: 6 }}>
          <Toggle
            label="Scatter"
            checked={scatterMode}
            onChange={(v) => set("scatterMode", v)}
          />
        </div>
      )}
      {scatterMode && (
        <SliderRow
          label="Per Element"
          value={mod.instancesPerElement ?? 1}
          min={1}
          max={16}
          step={1}
          displayValue={String(mod.instancesPerElement ?? 1)}
          parseValue={(s) =>
            Math.max(1, Math.min(16, Math.round(parseFloat(s))))
          }
          onChange={(v) =>
            set("instancesPerElement", Math.max(1, Math.min(16, Math.round(v))))
          }
          labelWidth={MOD_LABEL_WIDTH}
          accent={accent}
          dimChromeOnDrag
        />
      )}
      {placementMode !== "origin" && (
        <div style={{ marginTop: 6 }}>
          <Toggle
            label="Jitter"
            checked={mod.jitterEnabled ?? false}
            onChange={(v) => set("jitterEnabled", v)}
          />
        </div>
      )}
      {(mod.jitterEnabled ?? false) && placementMode !== "origin" && (
        <>
          <SliderRow
            label="Scale"
            value={mod.jitterScale ?? 0.25}
            min={0}
            max={1}
            step={0.01}
            displayValue={(mod.jitterScale ?? 0.25).toFixed(2)}
            parseValue={(s) => Math.max(0, Math.min(1, parseFloat(s)))}
            onChange={(v) => set("jitterScale", Math.max(0, Math.min(1, v)))}
            labelWidth={MOD_LABEL_WIDTH}
            accent={accent}
          />
          <SliderRow
            label="Rotation"
            value={mod.jitterRotation ?? 1.0}
            min={0}
            max={1}
            step={0.01}
            displayValue={(mod.jitterRotation ?? 1.0).toFixed(2)}
            parseValue={(s) => Math.max(0, Math.min(1, parseFloat(s)))}
            onChange={(v) => set("jitterRotation", Math.max(0, Math.min(1, v)))}
            labelWidth={MOD_LABEL_WIDTH}
            accent={accent}
          />
        </>
      )}
      <div style={{ marginTop: 6 }}>
        <Toggle
          label="Source Material"
          checked={mod.useSourceMaterial ?? false}
          onChange={(v) => set("useSourceMaterial", v)}
        />
      </div>
      <div style={{ marginTop: 6 }}>
        <Toggle
          label="Show Base"
          checked={mod.showBase ?? false}
          onChange={(v) => set("showBase", v)}
        />
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Shrink Wrap
// ---------------------------------------------------------------------------

const WRAP_MODE_OPTIONS = [
  { label: "Nearest Surface", value: "nearest_surface" as const },
  { label: "Nearest Vertex", value: "nearest_vertex" as const },
  { label: "Project", value: "project" as const },
];

const PROJECT_AXIS_OPTIONS = [
  { label: "X", value: "x" as const },
  { label: "Y", value: "y" as const },
  { label: "Z", value: "z" as const },
  { label: "Normal", value: "normal" as const },
];

function ShrinkwrapSettings({
  mod,
  set,
  objectName,
  accent,
}: ModSetterProps & {
  mod: BlendShrinkwrapModifier;
  objectName: string;
  accent: string;
}) {
  const sceneObjects = useStore((s) => s.sceneObjects);
  const targetOptions = useMemo(
    () =>
      Array.from(sceneObjects.values())
        .filter((obj) => obj.name !== objectName && obj.vertices.length > 0)
        .map((obj) => ({ value: obj.name, label: obj.name })),
    [sceneObjects, objectName],
  );

  const ownerVertexGroups = useStore(
    (s) => s.sceneObjects.get(objectName)?.vertexGroups,
  );
  const vertexGroupOptions = useMemo(
    () => toVertexGroupOptions(ownerVertexGroups),
    [ownerVertexGroups],
  );

  const wrapMode = mod.wrapMode ?? "nearest_surface";
  const diag = useObjectDiagonal(objectName);

  return (
    <>
      <SelectSearch
        label="Target"
        value={mod.targetObjectName ?? null}
        options={targetOptions}
        onChange={(value) => set("targetObjectName", value)}
        placeholder="None"
        eyedropperPick={(onPick, onCancel) => {
          beginSceneObjectNameEyedropper(
            onPick,
            onCancel,
            (obj) => obj.name !== objectName && obj.vertices.length > 0,
          );
        }}
      />
      <LabeledRow label="Mode" labelWidth={MOD_LABEL_WIDTH}>
        <SegmentedControl
          options={WRAP_MODE_OPTIONS}
          value={wrapMode}
          onChange={(v) => set("wrapMode", v)}
        />
      </LabeledRow>
      <SliderRow
        label="Offset"
        value={mod.offset ?? 0}
        min={-0.25 * diag}
        max={0.25 * diag}
        step={0.0005 * diag}
        displayValue={(mod.offset ?? 0).toFixed(3)}
        onChange={(v) => set("offset", v)}
        labelWidth={MOD_LABEL_WIDTH}
        accent={accent}
      />
      {wrapMode === "project" && (
        <>
          <LabeledRow label="Axis" labelWidth={MOD_LABEL_WIDTH}>
            <SegmentedControl
              options={PROJECT_AXIS_OPTIONS}
              value={mod.projectAxis ?? "normal"}
              onChange={(v) => set("projectAxis", v)}
            />
          </LabeledRow>
          <LabeledRow label="Direction" labelWidth={MOD_LABEL_WIDTH}>
            <ToggleChips
              items={[
                {
                  label: "+",
                  active: mod.projectPositive ?? true,
                  onClick: () =>
                    set("projectPositive", !(mod.projectPositive ?? true)),
                },
                {
                  label: "–",
                  active: mod.projectNegative ?? true,
                  onClick: () =>
                    set("projectNegative", !(mod.projectNegative ?? true)),
                },
              ]}
            />
          </LabeledRow>
        </>
      )}
      {vertexGroupOptions.length > 0 && (
        <SelectSearch
          label="Vertex Group"
          value={mod.vertexGroupId ?? null}
          options={vertexGroupOptions}
          onChange={(value) => set("vertexGroupId", value)}
          placeholder="All Vertices"
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Curve
// ---------------------------------------------------------------------------

const CURVE_AXIS_OPTIONS = [
  { label: "X", value: "x" as const },
  { label: "Y", value: "y" as const },
  { label: "Z", value: "z" as const },
];

function curveAxisBase(axis: CurveDeformAxis): "x" | "y" | "z" {
  if (axis.endsWith("x")) return "x";
  if (axis.endsWith("y")) return "y";
  return "z";
}

function curveAxisNegative(axis: CurveDeformAxis): boolean {
  return axis.startsWith("-");
}

function makeCurveAxis(
  base: "x" | "y" | "z",
  negative: boolean,
): CurveDeformAxis {
  return negative ? (`-${base}` as CurveDeformAxis) : base;
}

function CurveSettings({
  mod,
  set,
  objectName,
}: ModSetterProps & {
  mod: BlendCurveModifier;
  objectName: string;
  accent: string;
}) {
  const sceneObjects = useStore((s) => s.sceneObjects);
  const targetOptions = useMemo(
    () =>
      Array.from(sceneObjects.values())
        .filter((obj) => obj.name !== objectName && isCurveObject(obj))
        .map((obj) => ({ value: obj.name, label: obj.name })),
    [sceneObjects, objectName],
  );

  const axis = mod.deformAxis ?? "x";
  const baseAxis = curveAxisBase(axis);
  const reversed = curveAxisNegative(axis);
  const missingTarget =
    mod.targetObjectName !== null &&
    !Array.from(sceneObjects.values()).some(
      (obj) => obj.name === mod.targetObjectName && isCurveObject(obj),
    );

  return (
    <>
      <SelectSearch
        label="Target"
        value={mod.targetObjectName ?? null}
        options={targetOptions}
        onChange={(value) => set("targetObjectName", value)}
        placeholder="None"
        eyedropperPick={(onPick, onCancel) => {
          beginSceneObjectNameEyedropper(
            onPick,
            onCancel,
            (obj) => obj.name !== objectName && isCurveObject(obj),
          );
        }}
      />
      <LabeledRow label="Axis" labelWidth={MOD_LABEL_WIDTH}>
        <SegmentedControl
          options={CURVE_AXIS_OPTIONS}
          value={baseAxis}
          onChange={(value) =>
            set("deformAxis", makeCurveAxis(value, reversed))
          }
        />
      </LabeledRow>
      <div style={{ marginTop: 6 }}>
        <Toggle
          label="Reverse"
          checked={reversed}
          onChange={(value) =>
            set("deformAxis", makeCurveAxis(baseAxis, value))
          }
        />
      </div>
      <div style={{ marginTop: 6 }}>
        <Toggle
          label="Use Radius"
          checked={mod.useCurveRadius ?? false}
          onChange={(value) => set("useCurveRadius", value)}
        />
      </div>
      {missingTarget && (
        <div
          style={{
            marginTop: 6,
            fontSize: FONT_SIZE_SMALL,
            color: COLOR_TEXT_FAINT,
          }}
        >
          Target "{mod.targetObjectName}" is missing or is not a curve object.
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Boolean (CSG) modifier settings
// ---------------------------------------------------------------------------

const BOOLEAN_OP_OPTIONS: { label: string; value: BooleanOperation }[] = [
  { label: "Union", value: "union" },
  { label: "Difference", value: "difference" },
  { label: "Intersect", value: "intersect" },
];

function BooleanSettings({
  mod,
  set,
  objectName,
  accent,
}: ModSetterProps & {
  mod: BlendBooleanModifier;
  objectName: string;
  accent: string;
}) {
  const sceneObjects = useStore((s) => s.sceneObjects);
  const targetOptions = useMemo(
    () =>
      Array.from(sceneObjects.values())
        // Exclude the owner object so the user can't accidentally Boolean a
        // mesh with itself — CSG with self is undefined and would degrade to
        // pass-through, so hiding it at the picker level avoids the footgun.
        .filter((obj) => obj.name !== objectName && obj.vertices.length > 0)
        .map((obj) => ({ value: obj.name, label: obj.name })),
    [sceneObjects, objectName],
  );

  const [advancedOpen, setAdvancedOpen] = useState(false);
  const diag = useObjectDiagonal(objectName);

  const unresolvedTarget =
    mod.targetObjectName !== null &&
    !Array.from(sceneObjects.values()).some(
      (o) => o.name === mod.targetObjectName,
    );

  return (
    <>
      <LabeledRow label="Operation" labelWidth={MOD_LABEL_WIDTH}>
        <SegmentedControl
          options={BOOLEAN_OP_OPTIONS}
          value={mod.operation}
          onChange={(v) => set("operation", v)}
        />
      </LabeledRow>
      <SelectSearch
        label="Cutter"
        value={mod.targetObjectName ?? null}
        options={targetOptions}
        onChange={(value) => set("targetObjectName", value)}
        placeholder="None"
        eyedropperPick={(onPick, onCancel) => {
          beginSceneObjectNameEyedropper(
            onPick,
            onCancel,
            (obj) => obj.name !== objectName && obj.vertices.length > 0,
          );
        }}
      />
      {unresolvedTarget && (
        <div
          style={{
            fontSize: FONT_SIZE_SMALL,
            color: COLOR_TEXT_FAINT,
            padding: "4px 0",
          }}
        >
          Cutter "{mod.targetObjectName}" not found in scene.
        </div>
      )}
      <div style={{ marginTop: 4 }}>
        <Button
          type="button"
          onClick={() => setAdvancedOpen(!advancedOpen)}
          variant="ghost"
          size="xs"
          fullWidth
          style={{
            color: COLOR_TEXT_FAINT,
            justifyContent: "flex-start",
          }}
        >
          {advancedOpen ? "▾" : "▸"} Advanced
        </Button>
      </div>
      {advancedOpen && (
        <>
          <div style={{ marginTop: 6 }}>
            <Toggle
              label="Self Intersect"
              checked={mod.selfIntersect}
              onChange={(v) => set("selfIntersect", v)}
            />
          </div>
          <SliderRow
            label="Hole Tol."
            value={mod.holeTolerance}
            min={0}
            max={0.02 * diag}
            step={0.00002 * diag}
            displayValue={mod.holeTolerance.toFixed(4)}
            onChange={(v) => set("holeTolerance", v)}
            labelWidth={MOD_LABEL_WIDTH}
            accent={accent}
          />
        </>
      )}
    </>
  );
}

function FollowObjectSettings({
  mod,
  set,
  setMany,
  objectName,
}: ModSetterProps & {
  mod: BlendFollowObjectModifier;
  objectName: string;
  accent: string;
}) {
  const sceneObjects = useStore((s) => s.sceneObjects);

  const targetOptions = useMemo(
    () =>
      Array.from(sceneObjects.values())
        .filter((o) => o.name !== objectName)
        .map((o) => ({ value: o.name, label: o.name })),
    [sceneObjects, objectName],
  );

  const targetObj = useMemo(
    () =>
      mod.targetObjectName
        ? Array.from(sceneObjects.values()).find(
            (o) => o.name === mod.targetObjectName,
          )
        : undefined,
    [sceneObjects, mod.targetObjectName],
  );

  const isMannequin = !!targetObj && isMannequinObject(targetObj);

  const boneOptions = useMemo(() => {
    if (!isMannequin || !targetObj) return [];
    return Array.from(getBoneMap(targetObj.id)?.keys() ?? [])
      .sort()
      .map((name) => ({ value: name, label: name }));
  }, [isMannequin, targetObj]);

  return (
    <>
      <SelectSearch
        label="Object"
        value={mod.targetObjectName ?? null}
        options={targetOptions}
        onChange={(v) => setMany({ targetObjectName: v, targetBoneName: null })}
        placeholder="None"
        eyedropperPick={(onPick, onCancel) => {
          beginSceneObjectNameEyedropper(
            onPick,
            onCancel,
            (obj) => obj.name !== objectName,
          );
        }}
      />
      {isMannequin && (
        <SelectSearch
          label="Bone"
          value={mod.targetBoneName ?? null}
          options={boneOptions}
          onChange={(v) => set("targetBoneName", v)}
          placeholder="Root"
        />
      )}
      <div style={{ marginTop: 6 }}>
        <Toggle
          label="Position"
          checked={mod.followPosition}
          onChange={(v) => set("followPosition", v)}
        />
        <Toggle
          label="Rotation"
          checked={mod.followRotation}
          onChange={(v) => set("followRotation", v)}
        />
        <Toggle
          label="Scale"
          checked={mod.followScale}
          onChange={(v) => set("followScale", v)}
        />
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Noise
// ---------------------------------------------------------------------------

const NOISE_TYPE_OPTIONS: { label: string; value: NoiseType }[] = [
  { label: "Simplex", value: "simplex" },
  { label: "Ridged", value: "ridged" },
  { label: "Voronoi", value: "voronoi" },
  { label: "Worley", value: "worley" },
];

/** Generate a random 31-bit positive seed. Called only in response to an
 *  explicit user action (the dice button) — never during render, so it
 *  never breaks the determinism contract of the modifier. */
function randomSeed(): number {
  return Math.floor(Math.random() * 0x7fffffff) + 1;
}

function NoiseSettings({
  mod,
  set,
  accent,
  objectName,
}: ModSetterProps & {
  mod: BlendNoiseModifier;
  accent: string;
  objectName: string;
}) {
  const ownerVertexGroups = useStore(
    (s) => s.sceneObjects.get(objectName)?.vertexGroups,
  );
  const vertexGroupOptions = useMemo(
    () => toVertexGroupOptions(ownerVertexGroups),
    [ownerVertexGroups],
  );

  // Active layer is local UI state — the list is authoritative on the
  // modifier itself. Default to the first layer if one exists.
  const [activeLayerId, setActiveLayerId] = useState<string | null>(
    mod.layers[0]?.id ?? null,
  );

  // If the active layer disappears (deleted, or an override snap-back),
  // fall back to the first layer so the editor never points at nothing.
  const activeLayer = useMemo(
    () =>
      mod.layers.find((l) => l.id === activeLayerId) ?? mod.layers[0] ?? null,
    [mod.layers, activeLayerId],
  );

  /** Replace the layers array via `setModifierProperty`. The store merges
   *  the whole `layers` value into the override's `props`, so we pass a
   *  fully-rebuilt array rather than trying to splice in place. */
  const writeLayers = (next: NoiseLayer[]) => set("layers", next);

  /** Update a single layer by id, preserving everything else. */
  const updateLayer = (id: string, patch: Partial<NoiseLayer>) => {
    writeLayers(mod.layers.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  };

  const addLayer = () => {
    const nextIndex = mod.layers.length + 1;
    const layer = createDefaultNoiseLayer(nextIndex);
    writeLayers([...mod.layers, layer]);
    setActiveLayerId(layer.id);
  };

  const deleteLayer = (id: string) => {
    const next = mod.layers.filter((l) => l.id !== id);
    writeLayers(next);
    if (activeLayerId === id) setActiveLayerId(next[0]?.id ?? null);
  };

  const renameLayer = (id: string, name: string) => {
    const trimmed = name.trim();
    if (trimmed.length === 0) return;
    updateLayer(id, { name: trimmed });
  };

  // Items passed to ListControl. The trailing slot shows a per-layer
  // enable dot so users can see at a glance which layers are muted.
  const listItems = useMemo(
    () =>
      mod.layers.map((layer) => ({
        id: layer.id,
        label: layer.name,
        trailing: (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              updateLayer(layer.id, { enabled: !layer.enabled });
            }}
            title={layer.enabled ? "Mute layer" : "Unmute layer"}
            icon={
              layer.enabled ? (
                <EyeOpenIcon size={12} />
              ) : (
                <EyeClosedIcon size={12} />
              )
            }
            label={layer.enabled ? "Mute layer" : "Unmute layer"}
            size={20}
            style={{
              color: COLOR_TEXT_SUBTLE,
              opacity: layer.enabled ? 1 : 0.4,
              flexShrink: 0,
            }}
          />
        ),
      })),
    // `updateLayer` is a closure over `mod.layers`, so it's intentionally
    // excluded from deps — we only need to rebuild items when the layer
    // list itself changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mod.layers, updateLayer],
  );

  return (
    <>
      <ListControl
        items={listItems}
        activeId={activeLayer?.id ?? null}
        onSelect={setActiveLayerId}
        onAdd={addLayer}
        onDelete={deleteLayer}
        onRename={renameLayer}
        emptyMessage="No layers"
      />

      {activeLayer && (
        <div style={{ marginTop: 8 }}>
          <LabeledRow label="Type" labelWidth={MOD_LABEL_WIDTH}>
            <SegmentedControl
              options={NOISE_TYPE_OPTIONS}
              value={activeLayer.noiseType}
              onChange={(v) => updateLayer(activeLayer.id, { noiseType: v })}
            />
          </LabeledRow>

          <SliderRow
            label="Strength"
            value={activeLayer.strength}
            min={0}
            max={1}
            step={0.001}
            displayValue={`${(activeLayer.strength * 100).toFixed(1)}%`}
            onChange={(v) => updateLayer(activeLayer.id, { strength: v })}
            labelWidth={MOD_LABEL_WIDTH}
            accent={accent}
          />

          <SliderRow
            label="Scale"
            value={activeLayer.scale}
            min={0.1}
            max={20}
            step={0.1}
            displayValue={activeLayer.scale.toFixed(1)}
            onChange={(v) => updateLayer(activeLayer.id, { scale: v })}
            labelWidth={MOD_LABEL_WIDTH}
            accent={accent}
          />

          <LabeledRow label="Seed" labelWidth={MOD_LABEL_WIDTH}>
            <input
              type="number"
              value={activeLayer.seed}
              step={1}
              onChange={(e) => {
                const n = parseInt(e.target.value, 10);
                if (Number.isFinite(n))
                  updateLayer(activeLayer.id, { seed: n | 0 });
              }}
              style={{
                flex: 1,
                minWidth: 0,
                padding: "4px 6px",
                borderRadius: RADIUS_CONTROL,
                border: `0.5px solid ${COLOR_BORDER_STRONG}`,
                background: "transparent",
                color: COLOR_TEXT_SECONDARY,
                fontSize: FONT_SIZE_SMALL,
                fontFamily: "inherit",
              }}
            />
            <IconButton
              onClick={() =>
                updateLayer(activeLayer.id, { seed: randomSeed() })
              }
              title="Randomize seed"
              icon={<Dices size={12} />}
              label="Randomize seed"
              variant="secondary"
              size={24}
              style={{
                marginLeft: 6,
                background: "transparent",
                color: COLOR_TEXT_SUBTLE,
                flexShrink: 0,
              }}
            />
          </LabeledRow>

          <LabeledRow label="Axes" labelWidth={MOD_LABEL_WIDTH}>
            <ToggleChips
              items={[
                {
                  label: "X",
                  active: activeLayer.axisX,
                  onClick: () =>
                    updateLayer(activeLayer.id, { axisX: !activeLayer.axisX }),
                },
                {
                  label: "Y",
                  active: activeLayer.axisY,
                  onClick: () =>
                    updateLayer(activeLayer.id, { axisY: !activeLayer.axisY }),
                },
                {
                  label: "Z",
                  active: activeLayer.axisZ,
                  onClick: () =>
                    updateLayer(activeLayer.id, { axisZ: !activeLayer.axisZ }),
                },
              ]}
            />
          </LabeledRow>

          {vertexGroupOptions.length > 0 && (
            <SelectSearch
              label="Vertex Group"
              value={activeLayer.vertexGroupId ?? null}
              options={vertexGroupOptions}
              onChange={(value) =>
                updateLayer(activeLayer.id, { vertexGroupId: value })
              }
              placeholder="All Vertices"
            />
          )}
        </div>
      )}
    </>
  );
}
